import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import Tile from './Tile';
import type { CartItem } from '../pages/CartPage';
import TileSkeleton from './TileSkeleton';
import CatalogRow from './CatalogRow';
import { lifecycleBadge } from './catalogBadge';
import type { CatalogView } from '../hooks/useCatalogPrefs';

interface TileGridProps {
  tiles: TileConfig[];
  useBindingFlow?: boolean;
  onAddToCart?: (item: CartItem) => void;
  isAdminCatalog?: boolean;
  onNavigate?: (path: Route) => void;
  /** brief-26: 'list' is admin-catalogue only; storefront modes always get cards. */
  view?: CatalogView;
  pinnedIds?: string[];
  onTogglePin?: (id: string) => void;
}

interface TileGridSkeletonProps {
  count?: number;
}

export function TileGridSkeleton({ count = 6 }: TileGridSkeletonProps) {
  return (
    <div className="dp-grid">
      {Array.from({ length: count }, (_, i) => <TileSkeleton key={i} />)}
    </div>
  );
}

export default function TileGrid({
  tiles, useBindingFlow = false, onAddToCart, isAdminCatalog = false, onNavigate,
  view = 'tiles', pinnedIds = [], onTogglePin,
}: TileGridProps) {
  const isPinned = (id: string) => pinnedIds.includes(id);

  if (view === 'list' && isAdminCatalog) {
    return (
      <ul role="list" className="dp-list">
        {tiles.map((tile) => {
          const badge = lifecycleBadge(tile);
          return (
            <CatalogRow
              key={tile.id}
              tile={tile}
              badgeClass={badge.cls}
              badgeLabel={badge.label}
              pinned={isPinned(tile.id)}
              onTogglePin={() => onTogglePin?.(tile.id)}
              onNavigate={onNavigate}
            />
          );
        })}
      </ul>
    );
  }

  return (
    <div className="dp-grid">
      {tiles.map((tile) => (
        <Tile
          key={tile.id}
          tile={tile}
          useBindingFlow={useBindingFlow}
          onAddToCart={onAddToCart}
          isAdminCatalog={isAdminCatalog}
          onNavigate={onNavigate}
          pinned={isPinned(tile.id)}
          onTogglePin={isAdminCatalog && onTogglePin ? () => onTogglePin(tile.id) : undefined}
        />
      ))}
    </div>
  );
}
